import cloneDeep from "lodash/cloneDeep";
import helper_functions from "../../Config/helper_functions";
import type Konva from "konva";
import type { WatermarkItem } from "../../Redux/WaterMarks/watermarks";
import type { CreateTemplateTilePattern1Draft } from "../../Redux/UserSlice/userSlice";
import { defaultCreateTemplateTilePattern1 } from "../../Redux/UserSlice/userSlice";

interface CanvasDimensions {
  width: number;
  height: number;
}

export const getScaledFontSizeWithRespectToTargetScale = (
  text: string,
  fontFamily: string,
  canvasDimensions: CanvasDimensions,
  targetScale: number
): number => {
  // Calculates font size with respect to canvas dimensions
  const offscreenCanvas = document.createElement("canvas");
  offscreenCanvas.width = canvasDimensions.width;
  offscreenCanvas.height = canvasDimensions.height;
  const ctx = offscreenCanvas.getContext("2d");
  if (!ctx) return 10;
  ctx.font = `10px ${fontFamily}`;
  const lines = text.split('\n');
  const maxLineWidth = Math.max(...lines.map(line => ctx.measureText(line).width));
  const offscreenCanvasTextWidth = maxLineWidth;
  const targetWidth = canvasDimensions.width * targetScale;
  const scaleFactor = targetWidth / offscreenCanvasTextWidth;
  return 10 * scaleFactor;
}

interface CanvasImageSize {
  width: number;
  height: number;
}

/**
 * Module-level cache of decoded base photos keyed by URL. Switching back to a previously visited
 * batch image reuses the already-decoded HTMLImageElement instead of re-decoding (no async onload
 * round-trip), so revisits render instantly.
 *
 * Lives here rather than in canvas.tsx so `Utils/batchApply` can consult it without importing the
 * canvas component (which would be a cycle).
 */
export const batchImageCache = new Map<string, HTMLImageElement>();

/** Has this base photo already been decoded at least once this session? */
export const isBatchImageCached = (url: string | null | undefined): boolean =>
  !!url && batchImageCache.has(url);

/**
 * EWW-454: the same, for WATERMARK rasters (logos/signatures).
 *
 * Base photos were already cached above, but every batch switch re-entered the template branch
 * and built a brand-new `Image()` per watermark, re-decoding assets it had just displayed. That
 * decode is what the "Downloading Template" overlay was actually waiting on (no network request
 * is involved once the browser has the bytes -- the cost is decode + the onload round-trip).
 */
const watermarkRasterCache = new Map<string, HTMLImageElement>();

const isDecoded = (el: HTMLImageElement | undefined): el is HTMLImageElement =>
  !!el && el.complete && el.naturalWidth > 0;

/** Is this watermark raster already decoded and reusable right now? */
export const isWatermarkRasterCached = (src: string | null | undefined): boolean =>
  !!src && isDecoded(watermarkRasterCache.get(src));

/**
 * Decoded element for `src`, reusing the cached one when possible.
 *
 * Deliberately NOT routed through `imageProcessing`: that resolves only from `onload`, which
 * never fires for an element that is already `complete`, so a cached element handed to it would
 * hang forever. Callers pair this with `computeImageFit` instead.
 */
export const loadWatermarkRaster = (src: string): Promise<HTMLImageElement> => {
  const cached = watermarkRasterCache.get(src);
  if (isDecoded(cached)) return Promise.resolve(cached);

  return new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new window.Image();
    el.crossOrigin = "anonymous";
    el.onload = () => {
      watermarkRasterCache.set(src, el);
      resolve(el);
    };
    el.onerror = reject;
    el.src = src;
  });
};

/**
 * Synchronous best-effort read of a watermark's raster URL, for deciding whether anything still
 * needs decoding BEFORE committing to showing a loader. Mirrors `resolveWatermarkRasterSrc`
 * minus its async Blob/File branch, which is why it can return null where that one succeeds --
 * callers must treat null as "not known to be cached" (i.e. assume work is needed).
 */
export const peekWatermarkRasterSrc = (item: WatermarkItem): string | null => {
  const r = item as Record<string, unknown>;
  const s = r.src;
  if (typeof s === "string" && s.trim()) return s;
  if (s instanceof Blob || s instanceof File) return null;
  const m = r.mediaUrl;
  if (typeof m === "string" && m.trim()) return m;
  const media = r.media as { originalUrl?: unknown; url?: unknown } | undefined;
  const u = media?.originalUrl ?? media?.url;
  return typeof u === "string" && u.trim() ? u : null;
};

/**
 * Fit math for a DECODED image element. Single source of truth shared by `imageProcessing`
 * (async, waits for onload first) and the cached path above (element already decoded).
 */
export const computeImageFit = (
  el: HTMLImageElement,
  canvasImageSize: CanvasImageSize
): { scaleFactor: number; width: number; height: number } => {
  const width = el.width;
  const height = el.height;
  const { width: canvasIW, height: canvasIH } = canvasImageSize;
  const scaleFactor = width > height ? canvasIW / width : canvasIH / height;
  return { scaleFactor, width, height };
};

// Computes how an image watermark should be scaled to fit the currently rendered canvas image area
export const imageProcessing = (
  createImageElement: HTMLImageElement,
  canvasImageSize: CanvasImageSize
): Promise<{ scaleFactor: number; width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    createImageElement.onload = () =>
      resolve(computeImageFit(createImageElement, canvasImageSize));
    createImageElement.onerror = reject;
  });
};

interface DownloadBatchImageParams {
  imageToDownload: string;
  format?: string | number;
  fallbackFormat?: string | number;
  originalWidth: number;
  originalHeight: number;
  resolution?: number | string;
  quality?: number;
  setUrls: (value: unknown[] | ((prev: unknown[]) => unknown[])) => void;
}

type BatchLikeState = {
  batchurls?: Array<{ textOverrides?: Record<string, unknown> }>;
  currentIndex?: number | null;
  batchTextGlobalOverrides?: Record<string, unknown> | null;
};

type WatermarkStateLike = {
  items: WatermarkItem[];
  deletedWaterMarkIds?: string[];
  activeId?: string | null;
};

type UserStateLike = {
  template_Edit?: boolean;
  batchDownload?: boolean;
  create_Template?: boolean;
  edit_Template?: boolean;
};

type OffsetsLike = {
  paddingX?: number;
  paddingY?: number;
};

type ImageSizeLike = {
  width: number;
  height: number;
};

type MainImageStateLike = {
  batchurls: Array<{
    template?: string;
    landcsapeTemp?: string;
    portraitTemp?: string;
    watermarkSnapshot?: WatermarkItem[];
    tileSnapshot?: CreateTemplateTilePattern1Draft;
  }>;
  currentIndex: number | null;
  orientation: unknown;
};

type UserStateInitLike = {
  edit_Template?: boolean;
  create_Template?: boolean;
  batchDownload?: boolean;
  current_TemplateID?: unknown;
  template?: unknown;
};

type TemplateStateLike = {
  templates?: Array<{ id: string }>;
};

// Handles ArrowLeft/ArrowRight batch navigation and snapshot syncing.
export const handleKeyDown = ({
  event,
  batchUrlsLength,
  currentIndex,
  requestSwitch,
}: {
  event: KeyboardEvent;
  batchUrlsLength: number;
  currentIndex: number | null;
  // EWW-441: caller decides whether the switch happens immediately or gets blocked behind the
  // unapplied-changes gate -- this function only computes which index Left/Right means.
  requestSwitch: (newIndex: number) => void;
}) => {
  if (batchUrlsLength === 0) return;

  const activeElement = document.activeElement;
  const isInputFocused =
    activeElement &&
    (activeElement.tagName === "INPUT" ||
      activeElement.tagName === "TEXTAREA" ||
      (activeElement as HTMLElement).isContentEditable);
  if (isInputFocused) return;

  if (currentIndex === null) return;

  if (event.key === "ArrowLeft") {
    event.preventDefault();
    const newIndex = currentIndex > 0 ? currentIndex - 1 : batchUrlsLength - 1;
    if (newIndex !== currentIndex) requestSwitch(newIndex);
  } else if (event.key === "ArrowRight") {
    event.preventDefault();
    const newIndex = currentIndex < batchUrlsLength - 1 ? currentIndex + 1 : 0;
    if (newIndex !== currentIndex) requestSwitch(newIndex);
  }
};

// This function ensures each batch image can show different text values (or global replacements) 
// without mutating the base template permanently.
export const applyBatchTextOverrides = (
  item: WatermarkItem | { id?: string; type?: string; src?: string; [key: string]: unknown },
  mainImageState: BatchLikeState
): WatermarkItem => {
  if (!item || item.type !== "text") return item as WatermarkItem;
  if (!mainImageState?.batchurls?.length) return item as WatermarkItem;
  if (!item.id) return item as WatermarkItem;

  const idx =
    typeof mainImageState?.currentIndex === "number"
      ? mainImageState.currentIndex
      : null;
  const perImage =
    idx !== null ? mainImageState?.batchurls?.[idx]?.textOverrides : null;
  if (perImage && Object.prototype.hasOwnProperty.call(perImage, item.id)) {
    return { ...item, src: perImage[item.id] } as WatermarkItem;
  }

  const glob = mainImageState?.batchTextGlobalOverrides;
  if (glob && Object.prototype.hasOwnProperty.call(glob, item.id)) {
    return { ...item, src: glob[item.id] } as WatermarkItem;
  }

  return item as WatermarkItem;
};

// Preloads all text font families used by template watermarks before export.
export const loadTemplateFonts = async ({
  waterMarks,
  fontLoadCache,
}: {
  waterMarks: unknown[];
  fontLoadCache: Map<string, Promise<void>>;
}) => {
  try {
    if (typeof document === "undefined" || !document.fonts) return;
    if (!waterMarks?.length) return;

    const families = new Set<string>();
    for (const wm of waterMarks as any[]) {
      if (wm?.type !== "text") continue;
      const raw = wm?.font;
      const family = helper_functions.normalizeTemplateFontForRender(raw, wm?.font_id);
      if (family) families.add(family);
    }

    if (!families.size) return;

    const timeout = (ms: number) =>
      new Promise<void>((resolve) => setTimeout(resolve, ms));

    const loadFamily = (family: string) => {
      const existing = fontLoadCache.get(family);
      if (existing) return existing;

      const p = (async () => {
        try {
          await Promise.race([
            document.fonts.load(`10px "${family}"`),
            document.fonts.load(`10px ${family}`),
          ]);
        } catch {
          // ignore
        }

        try {
          await Promise.race([document.fonts.ready, timeout(800)]);
        } catch {
          // ignore
        }
      })();

      fontLoadCache.set(family, p);
      return p;
    };

    await Promise.all(Array.from(families).map(loadFamily));
  } catch {
    // ignore font loading issues; export will still happen.
  }
};

// Forces a stage redraw and waits one frame so text metrics settle before export.
export const waitForFontRender = async (stage: Konva.Stage | null) => {
  try {
    stage?.getLayers()?.forEach((layer) => {
      try {
        layer.batchDraw();
      } catch {
        // ignore
      }
    });
    stage?.draw();
  } catch {
    // ignore
  }

  await new Promise<void>((resolve) =>
    requestAnimationFrame(() => resolve())
  );
  await new Promise<void>((resolve) => setTimeout(resolve, 50));
};

// Exports either watermark-only PNGs or full-stage batch downloads from the hidden stage.
export const runExport = async ({
  stage,
  watermarksLayer,
  isBatchDownload,
  loadTemplateFontsFn,
  waitForFontRenderFn,
  onBatchDownload,
  onTemplateThumbnail,
  onSetCreateFalse,
  onSetCurrentIndex,
  urlsLength,
}: {
  stage: Konva.Stage | null;
  watermarksLayer: Konva.Layer | null;
  isBatchDownload: boolean;
  loadTemplateFontsFn: () => Promise<void>;
  waitForFontRenderFn: () => Promise<void>;
  onBatchDownload: (dataURL: string) => void;
  onTemplateThumbnail: (dataURL: string) => void;
  onSetCreateFalse: () => void;
  onSetCurrentIndex: (index: number) => void;
  urlsLength: number;
}) => {
  await loadTemplateFontsFn();
  await waitForFontRenderFn();
  if (!stage) {
    onSetCreateFalse();
    return;
  }

  // Template export must use only the watermarks layer. If the ref is still null (e.g. sticker-only
  // saves skip font loading and run before React commits the Layer ref), we would fall back to
  // full-stage export and bake in the base photo. SecondaryStageForDownload always stacks base
  // image on layer 0 and watermarks on layer 1.
  const layers = stage.getLayers?.() ?? [];
  const effectiveWatermarksLayer =
    watermarksLayer ??
    (!isBatchDownload && layers.length > 1 ? layers[1] : null);
  const exportStage = effectiveWatermarksLayer?.getStage?.() ?? stage;
    let dataURL: string | undefined;
    if (!isBatchDownload && effectiveWatermarksLayer) {
      dataURL = effectiveWatermarksLayer.toDataURL({
        mimeType: "image/png",
        quality: 1,
        x: 0,
        y: 0,
        width: exportStage.width(),
        height: exportStage.height(),
        pixelRatio: 1,
    });
  } else {
    dataURL = stage.toDataURL({
      mimeType: "image/png",
      quality: 1,
    });
  }
  if (!dataURL) {
    onSetCreateFalse();
    return;
  }

  if (isBatchDownload) {
    onBatchDownload(dataURL);
    onSetCreateFalse();
    onSetCurrentIndex(urlsLength + 1);
  } else {
    onTemplateThumbnail(dataURL);
  }
  onSetCreateFalse();
};

// Updates active selection and prepares preview crop data for non-create mode.
export const fetchData = async ({
  createMode,
  watermarkItemsLength,
  waterMarksLength,
  clearActiveSelection,
  stage,
  setImagePreviewSource,
  setCompletedCrop,
  offsets,
  canvasImageSize,
}: {
  createMode: boolean | null;
  watermarkItemsLength: number;
  waterMarksLength: number;
  clearActiveSelection: () => void;
  stage: Konva.Stage | null;
  setImagePreviewSource: (value: string) => void;
  setCompletedCrop: (value: {
    unit: "px";
    x: number;
    y: number;
    width: number;
    height: number;
  }) => void;
  offsets: { mainX: number; mainY: number };
  canvasImageSize: { width: number; height: number };
}) => {
  if (createMode === true && watermarkItemsLength === waterMarksLength) {
    clearActiveSelection();
  } else if (createMode === false) {
    if (stage && stage.attrs?.height !== 0) {
      const dataURL = stage.toDataURL({
        mimeType: "image/png",
        quality: 1,
      });

      setImagePreviewSource(dataURL);
      setCompletedCrop({
        unit: "px",
        x: offsets.mainX,
        y: offsets.mainY,
        width: canvasImageSize.width,
        height: canvasImageSize.height,
      });
    }
  }
};

// Loads and applies base image/template state, then synchronizes watermark/template data for the current frame.
export const initializeMainImage = async ({
  canvasWidth,
  canvasHeight: _canvasHeight,
  loadedMainImage,
  canvasImageSize,
  mainImageState,
  userState,
  templateState,
  watermarkState,
  setLoader,
  setMainImageCoordinates,
  setWaterMarks,
  setIsTemplateMode,
  deleteAllFn,
  setTemplateDataFn,
  setTemplateLoadedFn,
  setWaterMarksLoaderFn,
  getTemplateDataFromReduxFn,
  setScreenShotFn,
  setIsDownloadStarted,
  setMainImage,
  setTileDraftFn,
}: {
  canvasWidth: number | null;
  canvasHeight?: number | null;
  loadedMainImage: HTMLImageElement | null;
  canvasImageSize: { width: number; height: number };
  mainImageState: MainImageStateLike;
  userState: UserStateInitLike;
  templateState: TemplateStateLike;
  watermarkState: unknown;
  setLoader: (value: boolean) => void;
  /**
   * EWW-458: restores the incoming batch image's own tile settings into the shared "live" draft
   * (`userSlice.createTemplateTilePattern1`) that every renderer/UI control already reads from.
   * Optional so every existing non-batch caller of `initializeMainImage` is unaffected.
   */
  setTileDraftFn?: (tile: CreateTemplateTilePattern1Draft) => void;
  setMainImageCoordinates: (
    imgHeight: number,
    imgWidth: number,
    originalImgHeight: number,
    originalImgWidth: number
  ) => Promise<unknown>;
  setWaterMarks: (value: unknown[] | ((prev: unknown[]) => unknown[])) => void;
  setIsTemplateMode: (value: boolean) => void;
  deleteAllFn: () => void;
  setTemplateDataFn: (items: WatermarkItem[]) => void;
  setTemplateLoadedFn: (value: boolean) => void;
  setWaterMarksLoaderFn: (value: boolean) => void;
  getTemplateDataFromReduxFn: (
    id: string,
    templateStateArg: unknown,
    watermarkStateArg: unknown,
    fromEditor: boolean
  ) => unknown;
  setScreenShotFn: (payload: {
    type: "image";
    property: "template";
    id: string;
    x: number;
    y: number;
    deg: number;
    scaleX: number;
    scaleY: number;
    src?: string;
    opacity: number;
  }) => void;
  setIsDownloadStarted: (value: boolean) => void;
  setMainImage: (value: HTMLImageElement | null) => void;
}) => {
  setLoader(true);

  if (canvasWidth && loadedMainImage) {
    const imageHeight = loadedMainImage.height;
    const imageWidth = loadedMainImage.width;
    const displayImageWidth = canvasImageSize.width;
    const displayImageHeight = canvasImageSize.height;

    if (mainImageState.orientation !== false) {
      await setMainImageCoordinates(
        displayImageHeight,
        displayImageWidth,
        imageHeight,
        imageWidth
      );

      // In template authoring (create or edit) we must not wipe watermarks here:
      // - `edit_Template` keeps the existing template's watermarks on the canvas.
      // - `create_Template` already did a fresh `deleteAll()` at flow entry, and a canvas
      //   ratio switch re-runs this effect; wiping again would erase the user's in-progress
      //   watermarks. The legacy single-image flow keeps wiping exactly as before.
      // Batch mode is also excluded: wiping the local render list here (before we even know
      // what the new image's watermarks are) is what produced the old-frame/blank flash on
      // every Next click -- the batch branch below now only ever replaces `state.items` with
      // a complete, ready set, and the local list resyncs from that in one step.
      const isBatchFlow = mainImageState.batchurls.length > 0 && !userState.batchDownload;
      if (!userState.edit_Template && !userState.create_Template && !isBatchFlow) {
        setWaterMarks([]);
      }

      if (mainImageState.batchurls.length && !userState.batchDownload) {
        setIsTemplateMode(false);

        const index =
          typeof mainImageState.currentIndex === "number"
            ? mainImageState.currentIndex
            : 0;
        const url = mainImageState.batchurls[index];

        // EWW-458: restore THIS image's own tile settings into the shared live draft --
        // UNCONDITIONALLY (default/off for an image that has never been customized), not
        // "inherit whatever was live" as an earlier version of this did. Tile is now gated
        // exactly like a gesture edit (`batchTileEditPending`): a per-image `tileSnapshot` is
        // only ever written by an explicit Apply / Apply-to-all, never by mere navigation (see
        // `performBatchSwitch`). Inheriting the live draft for an un-committed image therefore
        // meant it could pick up whatever a DIFFERENT image's edit happened to leave live,
        // which is exactly what looked like "this photo adopted another photo's rotation."
        // Restoring unconditionally, every time, also makes this deterministic regardless of
        // visit order/history -- which is what lets the simple "did the index actually change"
        // guard in canvas.tsx's `setTileDraftFn` wrapper stay correct without tracking anything
        // beyond the immediately previous run.
        //
        // NOT the same rule as `watermarkSnapshot` just below, and deliberately so: normal
        // watermark items are a genuinely shared, carry-forward-by-default concept in this
        // codebase (the same items apply to every photo unless customized) -- tile is not.
        setTileDraftFn?.(url?.tileSnapshot ?? defaultCreateTemplateTilePattern1);

        if (url?.watermarkSnapshot != null) {
          // `setTemplateDataFn` fully replaces the watermark items in one dispatch -- no
          // separate `deleteAllFn()` clear-to-empty first, so the outgoing image's watermarks
          // stay on screen (paired with the outgoing image, still a fully consistent frame)
          // right up until the new ones are ready.
          const snapshotWithOverrides = cloneDeep(url.watermarkSnapshot).map(
            (item) => applyBatchTextOverrides(item, mainImageState as BatchLikeState)
          );
          setTemplateDataFn(snapshotWithOverrides);
          setTemplateLoadedFn(true);
          setWaterMarksLoaderFn(false);
        } else {
          const templateId =
            url?.template || url?.landcsapeTemp || url?.portraitTemp;

          if (templateId) {
            const templateInSlice = (templateState.templates as Array<{ id: string }>).find(
              (t) => t.id === templateId
            );

            if (templateInSlice) {
              // Awaited so `setMainImage(loadedMainImage)` below (which is what actually swaps
              // the visible base photo) doesn't fire until this fully resolves and dispatches
              // the new watermark set -- otherwise the new photo would appear first, still
              // paired with the outgoing image's watermarks, for one visible frame. Wrapped in
              // try/catch so a rejected fetch (network error, decrypt failure, etc.) still lets
              // the function fall through to `setMainImage`/`setLoader(false)` below instead of
              // leaving the canvas stuck on the outgoing image forever.
              try {
                await getTemplateDataFromReduxFn(
                  templateId,
                  templateState,
                  watermarkState,
                  false
                );
              } catch (error) {
                console.error("Failed to load template watermark data:", error);
              }
            } else {
              const rawTempID = userState.current_TemplateID;
              const tempID =
                rawTempID && typeof rawTempID === "object"
                  ? mainImageState.orientation === "landscape"
                    ? (rawTempID as { landcsapeTemp?: string; portraitTemp?: string }).landcsapeTemp ||
                      (rawTempID as { landcsapeTemp?: string; portraitTemp?: string }).portraitTemp
                    : (rawTempID as { portraitTemp?: string; landcsapeTemp?: string }).portraitTemp ||
                      (rawTempID as { portraitTemp?: string; landcsapeTemp?: string }).landcsapeTemp
                  : rawTempID;

              const templateRecord = userState.template as unknown as Array<{
                id: string;
                media?: { originalUrl?: string };
              }>;
              const selectedTemplate = Array.isArray(templateRecord)
                ? templateRecord.find((t) => t.id === tempID)
                : undefined;

              if (selectedTemplate) {
                // `setScreenShotFn` also fully replaces `state.items` (with the template
                // screenshot placeholder), so no separate clear is needed here either.
                setScreenShotFn({
                  type: "image",
                  property: "template",
                  id: String(Date.now()),
                  x: 50,
                  y: 50,
                  deg: 0,
                  scaleX: 1,
                  scaleY: 1,
                  src: selectedTemplate.media?.originalUrl,
                  opacity: 1,
                });
              } else {
                // No resolvable template data at all for this image -- nothing above will
                // replace `state.items`, so clear explicitly here instead of relying on an
                // eager clear that ran even when replacement data WAS coming.
                deleteAllFn();
                // EWW-458: same gap as the sibling `deleteAllFn()` a few lines below -- this
                // exit never cleared the loader either.
                setWaterMarksLoaderFn(false);
              }
            }
          } else {
            console.log("No template ID found in URL");
            deleteAllFn();
            // EWW-458: every OTHER branch in this if/else clears the loader (directly, or via
            // `GetTemplateDataFromRedux`'s own success/error paths) -- this was the one that
            // didn't. A batch image with neither a `watermarkSnapshot` NOR a resolvable
            // template (exactly what a freshly-uploaded photo with only tile settings looks
            // like) left `waterMarksLoader` stuck true forever, which is what
            // `downloadingTemplateOverlay` -> `hideCanvasContent` reads to blank the canvas.
            setWaterMarksLoaderFn(false);
          }
        }
      } else if (mainImageState.batchurls.length && userState.batchDownload) {
        const index =
          typeof mainImageState.currentIndex === "number"
            ? mainImageState.currentIndex
            : 0;
        const batchUrl = mainImageState.batchurls[index];
        const id = batchUrl?.template
          ? batchUrl.template
          : mainImageState.orientation === "landscape" && batchUrl?.landcsapeTemp
            ? batchUrl.landcsapeTemp
            : batchUrl?.portraitTemp || batchUrl?.landcsapeTemp;

        const result = getTemplateDataFromReduxFn(
          id ?? "",
          templateState,
          watermarkState,
          false
        );

        if (result) {
          setIsDownloadStarted(true);
          setIsTemplateMode(true);
        }
      }

      setMainImage(loadedMainImage);
    }
  }

  setLoader(false);
};

// Dispatches the final cropped image payload with the correct output dimensions for download/template save.
export const downloadImage = ({
  imageUri,
  resolution,
  canvasImageSize,
  imageSize,
  setImageFn,
  setCreateFalse,
}: {
  imageUri: string;
  resolution?: number | string;
  canvasImageSize: { width: number; height: number };
  imageSize: { width: number; height: number };
  setImageFn: (payload: {
    img: string;
    height: number;
    width: number;
    readyTodownload: boolean;
  }) => void;
  setCreateFalse: () => void;
}) => {
  const resolutionNum = Number(resolution);
  const hasPresetResolution =
    Number.isFinite(resolutionNum) && resolutionNum > 0;
  const outW = hasPresetResolution
    ? canvasImageSize.width
    : imageSize.width > 0
      ? imageSize.width
      : canvasImageSize.width;
  const outH = hasPresetResolution
    ? canvasImageSize.height
    : imageSize.height > 0
      ? imageSize.height
      : canvasImageSize.height;

  setImageFn({
    img: imageUri,
    height: outH,
    width: outW,
    readyTodownload: true,
  });
  setCreateFalse();
};

// Converts Redux watermark items into canvas-ready objects with image elements and pixel coords.
export const processWatermark = async ({
  item,
  canvasDimensions,
  canvasImageSize,
  scaleFactorMaxFn,
}: {
  item: WatermarkItem;
  canvasDimensions: { width: number | null; height: number | null };
  canvasImageSize: CanvasImageSize;
  scaleFactorMaxFn: (
    properties: Partial<WatermarkItem> & { id: string },
    tag?: string
  ) => void;
}): Promise<WatermarkItem | null> => {
  let processedWatermark: WatermarkItem | undefined;

  const cw = canvasDimensions.width ?? 0;
  const ch = canvasDimensions.height ?? 0;
  const pixelCoords = helper_functions.percent_To_pixel(
    Number(item.x) || 0,
    Number(item.y) || 0,
    cw,
    ch
  );

  if (item.type === "image" || item.type === "signature") {
    const src = await resolveWatermarkRasterSrc(item);
    if (!src) return null;

    const imageElement = await loadWatermarkRaster(src);
    const { scaleFactor } = computeImageFit(imageElement, canvasImageSize);
    processedWatermark = {
      ...item,
      x: pixelCoords.x,
      y: pixelCoords.y,
      image: imageElement,
      alignEnable: item.alignEnable,
    };

    const noScaleFactor =
      !item.hasOwnProperty("scaleFactor") && !item.hasOwnProperty("ScaleApi");
    const hasExplicitScale =
      Number.isFinite(Number(item.scaleX)) &&
      Number(item.scaleX) > 0 &&
      Number.isFinite(Number(item.scaleY)) &&
      Number(item.scaleY) > 0;
    const fallbackScale = scaleFactor / 2;
    const effectiveScaleX = hasExplicitScale ? Number(item.scaleX) : fallbackScale;
    const effectiveScaleY = hasExplicitScale ? Number(item.scaleY) : fallbackScale;

    // Keep user-provided watermark size stable (especially in batch snapshots and image uploads).
    // Auto-derive default scale only when no explicit scale exists.
    if (item.property !== "template" && noScaleFactor && !hasExplicitScale) {
      scaleFactorMaxFn(
        {
          ...item,
          scaleX: fallbackScale,
          scaleY: fallbackScale,
          scaleFactor: scaleFactor * 2,
        },
        "!== template"
      );
    } else if (
      item.property === "image" &&
      !item.hasOwnProperty("ScaleApi")
    ) {
      processedWatermark = {
        ...processedWatermark,
        scaleX: effectiveScaleX,
        scaleY: effectiveScaleY,
        scaleFactor: (item.scaleFactor as number) ?? scaleFactor * 2,
      };
    }
  } else if (item.type === "text") {
    processedWatermark = {
      ...item,
      x: pixelCoords.x,
      y: pixelCoords.y,
    };
  }

  return processedWatermark;
};

/** Redux keeps containerWidth/Hight in editor pixels; they go stale when the template aspect
 *  preset changes. Spreading them over local state overwrites Konva's measured box before
 *  Text remeasures (date watermarks often skip the scaleFactor-driven remeasure path). That
 *  mismatch can corrupt the next pixel→percent write and shift the watermark after a 1:1
 *  round-trip. */
const reduxTextItemWithoutStalePixelBox = (item: WatermarkItem): WatermarkItem => {
  const {
    containerWidth: _cw,
    containerHight: _ch,
    sizeOfContainer: _soc,
    ...rest
  } = item as WatermarkItem & { sizeOfContainer?: number };
  return rest as WatermarkItem;
};

export const deduplicateById = (items: WatermarkItem[]): WatermarkItem[] => {
  const seen = new Set<string>();
  const result: WatermarkItem[] = [];
  for (const item of items) {
    if (item && item.id && !seen.has(item.id)) {
      seen.add(item.id);
      result.push(item);
    }
  }
  return result;
};

// Reconciles local canvas watermarks with Redux changes for add/remove/update batch flows.
export const updateWatermarks = async ({
  watermarkState,
  waterMarks,
  canvasDimensions,
  mainImageState,
  userState,
  isDownloadStarted,
  offsets,
  isImageSquare,
  imageSize,
  setLoader,
  setWaterMarks,
  setCreateTrue,
  setIsDownloadStarted,
  processWatermarkFn,
}: {
  watermarkState: WatermarkStateLike | null | undefined;
  waterMarks: unknown[];
  canvasDimensions: { width: number | null; height: number | null };
  mainImageState: BatchLikeState;
  userState: UserStateLike;
  isDownloadStarted: boolean;
  offsets: OffsetsLike;
  isImageSquare: boolean;
  imageSize: ImageSizeLike;
  setLoader: (value: boolean) => void;
  setWaterMarks: (value: WatermarkItem[] | ((prev: unknown[]) => unknown[])) => void;
  setCreateTrue: () => void;
  setIsDownloadStarted: (value: boolean) => void;
  processWatermarkFn: (item: WatermarkItem) => Promise<WatermarkItem | null>;
}) => {
  if (!watermarkState) return;

  const reduxWatermarkIds = watermarkState.items?.map((item) => item.id);
  const localWatermarkIds = (waterMarks as WatermarkItem[])?.map((item) => item.id);
  const deletedIds = watermarkState.deletedWaterMarkIds || [];

  // Redux has more items than the local list -- something was added, so process and append it.
  if (reduxWatermarkIds.length > localWatermarkIds.length) {
    const newIds = reduxWatermarkIds.filter(
      (id) => !localWatermarkIds.includes(id)
    );

    const newWatermarks = await Promise.all(
      watermarkState.items
        .filter((item) => newIds.includes(item.id))
        .map(async (item) => {
          const processedWatermark = await processWatermarkFn(
            applyBatchTextOverrides(item, mainImageState)
          );
          return processedWatermark;
        })
    );

    setWaterMarks((prev) => {
      const prevItems = prev as WatermarkItem[];
      const existingIds = new Set(prevItems.map((wm) => wm.id));
      const filteredNew = newWatermarks.filter(
        (wm) => wm.id && !existingIds.has(wm.id) && !deletedIds.includes(wm.id)
      );
      const combined = prevItems
        .filter((wm) => !deletedIds.includes(wm.id))
        .concat(filteredNew);
      return deduplicateById(combined);
    });
  } else if (reduxWatermarkIds.length < localWatermarkIds.length) {
    // Fewer items in Redux -- something was deleted, so drop it from the local list too.
    const removedIds = localWatermarkIds.filter(
      (id) => !reduxWatermarkIds.includes(id)
    );

    setWaterMarks((prevWatermarks) =>
      deduplicateById(
        (prevWatermarks as WatermarkItem[]).filter(
          (item) =>
            item.id && !removedIds.includes(item.id) && !deletedIds.includes(item.id)
        )
      )
    );
  } else if (reduxWatermarkIds.length === localWatermarkIds.length) {
    // Same count, but the ids differ -- one item was swapped for another (e.g. undo/redo,
    // or a batch snapshot restore), so replace it in place instead of add/remove.
    const newIds = reduxWatermarkIds.filter((id) => !localWatermarkIds.includes(id));
    const removedIds = localWatermarkIds.filter((id) => !reduxWatermarkIds.includes(id));

    if (newIds.length > 0) {
      const processedReplacements = await Promise.all(
        watermarkState.items
          .filter((item) => newIds.includes(item.id))
          .map(async (item) => {
            const processed = await processWatermarkFn(
              applyBatchTextOverrides(item, mainImageState)
            );
            return processed;
          })
      );
      const valid = processedReplacements.filter(
        (wm): wm is WatermarkItem =>
          wm != null && wm.id != null && !deletedIds.includes(wm.id)
      );

      setWaterMarks((prev) => {
        const cw = canvasDimensions.width ?? 0;
        const ch = canvasDimensions.height ?? 0;
        const validIds = new Set(valid.map((v) => v.id));
        const prevItems = (prev as WatermarkItem[]).filter(
          (wm) =>
            wm.id &&
            !removedIds.includes(wm.id) &&
            !deletedIds.includes(wm.id) &&
            !validIds.has(wm.id)
        );
        const mergedKept = prevItems.map((wm) => {
          const rawItem = watermarkState.items.find((it) => it.id === wm.id);
          if (!rawItem) return wm;
          const item = applyBatchTextOverrides(rawItem, mainImageState);
          const cords = helper_functions.percent_To_pixel(
            Number(item.x) || 0,
            Number(item.y) || 0,
            cw,
            ch
          );
          const templateTextMerge =
            (userState.create_Template || userState.edit_Template) &&
            item.type === "text" &&
            !item.flexible_frame;
          const reduxPayload = templateTextMerge
            ? reduxTextItemWithoutStalePixelBox(item as WatermarkItem)
            : item;
          return {
            ...wm,
            ...reduxPayload,
            x: cords.x,
            y: cords.y,
          } as WatermarkItem;
        });
        return deduplicateById([...mergedKept, ...valid]);
      });
    } else if (
      watermarkState.activeId ||
      !isDownloadStarted ||
      userState.create_Template ||
      userState.edit_Template
    ) {
      const waterMarksItems = waterMarks as WatermarkItem[];
      const updatedWatermarks = await Promise.all(
        (watermarkState.items ?? []).map(async (rawItem) => {
          const item = applyBatchTextOverrides(rawItem, mainImageState);
          const index = waterMarksItems.findIndex(
            (watermark) => watermark.id === item.id
          );
          if (index !== -1) {
            const cw = canvasDimensions.width ?? 0;
            const ch = canvasDimensions.height ?? 0;
            const cords = helper_functions.percent_To_pixel(
              Number(item.x) || 0,
              Number(item.y) || 0,
              cw,
              ch
            );
            // In template authoring, do not re-run full processing for an already-loaded
            // image/signature on each sync. Reprocessing derives scale from the current
            // cover-fit frame and can overwrite user-visible size after ratio switches.
            const localWm = waterMarksItems[index];
            const hasLocalRaster =
              (item.type === "image" || item.type === "signature") &&
              localWm &&
              "image" in localWm &&
              localWm.image != null;
            if (
              (userState.create_Template || userState.edit_Template) &&
              (item.type === "image" || item.type === "signature") &&
              !item.flexible_frame &&
              !hasLocalRaster
            ) {
              const processed = (await processWatermarkFn(item)) as WatermarkItem | null;
              if (processed) return processed;
            }
            const templateTextMerge =
              (userState.create_Template || userState.edit_Template) &&
              item.type === "text" &&
              !item.flexible_frame;
            // An align button (top-left, center, etc.) sets this watermark's position live on
            // the canvas, not through Redux -- so keep the on-screen x/y instead of overwriting
            // it with Redux's (stale) percent coords below.
            const alignedTemplateImage =
              (userState.create_Template || userState.edit_Template) &&
              (item.type === "image" || item.type === "signature") &&
              !item.flexible_frame &&
              String((item as { alignEnable?: unknown }).alignEnable ?? "") !== "" &&
              String((item as { alignEnable?: unknown }).alignEnable ?? "") !== "None";
            const reduxPayload = templateTextMerge
              ? reduxTextItemWithoutStalePixelBox(item as WatermarkItem)
              : item;
            const nextX = alignedTemplateImage
              ? Number((waterMarksItems[index] as WatermarkItem)?.x ?? cords.x)
              : cords.x;
            const nextY = alignedTemplateImage
              ? Number((waterMarksItems[index] as WatermarkItem)?.y ?? cords.y)
              : cords.y;
            return {
              ...waterMarksItems[index],
              ...reduxPayload,
              x: nextX,
              y: nextY,
            } as WatermarkItem;
          }
          return ((await processWatermarkFn(item)) ?? item) as WatermarkItem;
        })
      );
      const filteredWatermarks = updatedWatermarks.filter(
        (wm): wm is WatermarkItem =>
          wm != null && wm.id != null && !deletedIds.includes(wm.id)
      );
      setWaterMarks(deduplicateById(filteredWatermarks));
    } else if (
      isDownloadStarted &&
      userState.batchDownload &&
      (offsets.paddingX || offsets.paddingY || isImageSquare) &&
      imageSize.height &&
      imageSize.width
    ) {
      setCreateTrue();
      setIsDownloadStarted(false);
    }
  }
};

/** String for `Image.src`: data URL, blob URL, or http(s). Falls back when `src` is empty/`{}` but storage URL exists. */
export const resolveWatermarkRasterSrc = async (
  item: WatermarkItem
): Promise<string | null> => {
  const r = item as Record<string, unknown>;
  const s = r.src;
  if (typeof s === "string" && s.trim()) return s;
  if (s instanceof Blob || s instanceof File) {
    try {
      return URL.createObjectURL(s);
    } catch {
      const d = await helper_functions.convertToDataURL(s as File);
      return typeof d === "string" ? d : null;
    }
  }
  if (s != null && typeof s === "object") {
    const m = r.mediaUrl;
    if (typeof m === "string" && m.trim()) return m;
  }
  const m = r.mediaUrl;
  if (typeof m === "string" && m.trim()) return m;
  const media = r.media as { originalUrl?: unknown; url?: unknown } | undefined;
  const u = media?.originalUrl ?? media?.url;
  return typeof u === "string" && u.trim() ? u : null;
};

export const downloadBatchImage = async ({
  imageToDownload,
  format,
  fallbackFormat,
  originalWidth,
  originalHeight,
  resolution,
  quality,
  setUrls,
}: DownloadBatchImageParams) => {
  let targetWidth: number;
  let targetHeight: number;

  const selectedFormat = String(format || fallbackFormat || "png");
  const resolutionNum = resolution ? Number(resolution) : 0;

  if (resolutionNum > 0) {
    targetWidth = resolutionNum;
    targetHeight = (resolutionNum / originalWidth) * originalHeight;
  } else {
    targetWidth = originalWidth;
    targetHeight = originalHeight;
  }

  const resizedImage = await helper_functions.resizeFile(
    imageToDownload,
    targetWidth,
    targetHeight,
    selectedFormat,
    quality
  );

  const fileName = `image.${selectedFormat}`;
  setUrls((prevUrls) => [...prevUrls, fileName]);

  const link = document.createElement("a");
  link.download = fileName;
  link.href = resizedImage;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
